import React, { useState, useEffect, useRef } from 'react';

export const HowItWorksJourney = ({
  sectionTitle = "How it works",
  backgroundColor = "#f7f5f3",
  step1Title = "Join",
  step1Desc = "Create and verify your profile.",
  step1ImageUrl = "",
  step2Title = "Stay connected",
  step2Desc = "We'll give you ways to share your experience.",
  step2ImageUrl = "",
  step3Title = "Share your experience",
  step3Desc = "Through questions, conversations, or reflections—on your schedule.",
  step3ImageUrl = "",
  step4Title = "Get rewarded",
  step4Desc = "Your experience is valued and respected here.",
  step4ImageUrl = ""
}) => {
  const steps = [
    { num: "1", title: step1Title, desc: step1Desc, imageUrl: step1ImageUrl, align: "right", screen: "1" },
    { num: "2", title: step2Title, desc: step2Desc, imageUrl: step2ImageUrl, align: "left", screen: "2" },
    { num: "3", title: step3Title, desc: step3Desc, imageUrl: step3ImageUrl, align: "right", screen: "3" },
    { num: "4", title: step4Title, desc: step4Desc, imageUrl: step4ImageUrl, align: "left", screen: "4" }
  ];

  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const renderStepTitle = (title) => title;

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = rect.height;
      const viewportHeight = window.innerHeight;

      // Delay the start significantly - don't begin until section is well into view
      const scrolled = viewportHeight - rect.top;
      const startThreshold = viewportHeight * 0.6; // Don't start until 60% past top (much later)
      const adjustedScrolled = Math.max(0, scrolled - startThreshold);
      // Spread the progress over a larger scroll distance
      const totalScrollDistance = sectionHeight * 0.5;
      const progress = Math.max(0, Math.min(1, adjustedScrolled / totalScrollDistance));

      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // iPhone mockup frames for each step - compact version for 2x2 grid
  const StepIcon = ({ step }) => {
    const iPhoneFrame = (screenContent) => (
      <svg width="120" height="240" viewBox="0 0 180 360" fill="none">
        {/* iPhone outer frame */}
        <rect x="2" y="2" width="176" height="356" rx="28" fill="#1a1a1a" stroke="#333" strokeWidth="2"/>
        {/* Screen area */}
        <rect x="8" y="8" width="164" height="344" rx="24" fill="#f7f5f3"/>
        {/* Dynamic Island */}
        <rect x="60" y="14" width="60" height="20" rx="10" fill="#1a1a1a"/>
        {/* Screen content */}
        <g transform="translate(8, 40)">
          {screenContent}
        </g>
        {/* Home indicator */}
        <rect x="60" y="340" width="60" height="4" rx="2" fill="#ccc"/>
      </svg>
    );

    const screens = {
      "1": iPhoneFrame(
        // Profile creation screen
        <>
          <text x="82" y="15" textAnchor="middle" fill="#272726" fontSize="11" fontWeight="600">Join Real Patients</text>
          {/* Avatar placeholder */}
          <circle cx="82" cy="55" r="28" fill="#dbe4f3" stroke="#272726" strokeWidth="1.5"/>
          <circle cx="82" cy="50" r="12" fill="white" stroke="#272726" strokeWidth="1.5"/>
          <path d="M68 65 Q82 80 96 65" fill="none" stroke="#272726" strokeWidth="1.5"/>
          <text x="82" y="100" textAnchor="middle" fill="#272726" fontSize="9">Add photo</text>
          {/* Form fields */}
          <rect x="12" y="115" width="140" height="28" rx="6" fill="white" stroke="#ddd" strokeWidth="1"/>
          <text x="20" y="133" fill="#999" fontSize="9">Full name</text>
          <rect x="12" y="150" width="140" height="28" rx="6" fill="white" stroke="#ddd" strokeWidth="1"/>
          <text x="20" y="168" fill="#999" fontSize="9">Email</text>
          <rect x="12" y="185" width="140" height="28" rx="6" fill="white" stroke="#ddd" strokeWidth="1"/>
          <text x="20" y="203" fill="#999" fontSize="9">Condition</text>
          {/* CTA button */}
          <rect x="12" y="235" width="140" height="36" rx="8" fill="#f15b41"/>
          <text x="82" y="258" textAnchor="middle" fill="white" fontSize="11" fontWeight="600">Continue</text>
        </>
      ),
      "2": iPhoneFrame(
        // Matching/opportunities screen
        <>
          <text x="82" y="15" textAnchor="middle" fill="#272726" fontSize="11" fontWeight="600">Your Matches</text>
          {/* Match cards */}
          <rect x="12" y="30" width="140" height="70" rx="8" fill="white" stroke="#dbe4f3" strokeWidth="2"/>
          <circle cx="38" cy="55" r="14" fill="#dbe4f3"/>
          <text x="38" y="59" textAnchor="middle" fill="#272726" fontSize="10">🔬</text>
          <text x="60" y="48" fill="#272726" fontSize="9" fontWeight="600">Research Study</text>
          <text x="60" y="60" fill="#666" fontSize="7">30 min survey</text>
          <rect x="60" y="68" width="40" height="16" rx="4" fill="#e8f5e9"/>
          <text x="80" y="79" textAnchor="middle" fill="#2e7d32" fontSize="7" fontWeight="600">$75</text>

          <rect x="12" y="108" width="140" height="70" rx="8" fill="white" stroke="#fce8e3" strokeWidth="2"/>
          <circle cx="38" cy="133" r="14" fill="#fce8e3"/>
          <text x="38" y="137" textAnchor="middle" fill="#272726" fontSize="10">💬</text>
          <text x="60" y="126" fill="#272726" fontSize="9" fontWeight="600">Focus Group</text>
          <text x="60" y="138" fill="#666" fontSize="7">1 hour video call</text>
          <rect x="60" y="146" width="40" height="16" rx="4" fill="#e8f5e9"/>
          <text x="80" y="157" textAnchor="middle" fill="#2e7d32" fontSize="7" fontWeight="600">$150</text>

          <rect x="12" y="186" width="140" height="70" rx="8" fill="white" stroke="#fff3e0" strokeWidth="2"/>
          <circle cx="38" cy="211" r="14" fill="#fff3e0"/>
          <text x="38" y="215" textAnchor="middle" fill="#272726" fontSize="10">📋</text>
          <text x="60" y="204" fill="#272726" fontSize="9" fontWeight="600">Advisory Board</text>
          <text x="60" y="216" fill="#666" fontSize="7">Quarterly meetings</text>
          <rect x="60" y="224" width="40" height="16" rx="4" fill="#e8f5e9"/>
          <text x="80" y="235" textAnchor="middle" fill="#2e7d32" fontSize="7" fontWeight="600">$500</text>
        </>
      ),
      "3": iPhoneFrame(
        // Survey/interview screen
        <>
          <text x="82" y="15" textAnchor="middle" fill="#272726" fontSize="11" fontWeight="600">Share Expertise</text>
          {/* Progress bar */}
          <rect x="12" y="28" width="140" height="6" rx="3" fill="#eee"/>
          <rect x="12" y="28" width="95" height="6" rx="3" fill="#f15b41"/>
          <text x="152" y="33" fill="#666" fontSize="7">4/6</text>
          {/* Question */}
          <text x="12" y="55" fill="#272726" fontSize="10" fontWeight="600">How has your condition</text>
          <text x="12" y="68" fill="#272726" fontSize="10" fontWeight="600">affected your daily life?</text>
          {/* Answer options */}
          <rect x="12" y="85" width="140" height="32" rx="6" fill="white" stroke="#ddd" strokeWidth="1"/>
          <circle cx="28" cy="101" r="8" fill="white" stroke="#f15b41" strokeWidth="2"/>
          <text x="44" y="105" fill="#272726" fontSize="8">Significantly impacted</text>

          <rect x="12" y="124" width="140" height="32" rx="6" fill="#fce8e3" stroke="#f15b41" strokeWidth="2"/>
          <circle cx="28" cy="140" r="8" fill="#f15b41"/>
          <circle cx="28" cy="140" r="3" fill="white"/>
          <text x="44" y="144" fill="#272726" fontSize="8">Moderately impacted</text>

          <rect x="12" y="163" width="140" height="32" rx="6" fill="white" stroke="#ddd" strokeWidth="1"/>
          <circle cx="28" cy="179" r="8" fill="white" stroke="#ddd" strokeWidth="2"/>
          <text x="44" y="183" fill="#272726" fontSize="8">Minimally impacted</text>
          {/* Next button */}
          <rect x="12" y="220" width="140" height="36" rx="8" fill="#f15b41"/>
          <text x="82" y="243" textAnchor="middle" fill="white" fontSize="11" fontWeight="600">Next Question</text>
        </>
      ),
      "4": iPhoneFrame(
        // Earnings/payment screen
        <>
          <text x="82" y="15" textAnchor="middle" fill="#272726" fontSize="11" fontWeight="600">Your Earnings</text>
          {/* Total earnings card */}
          <rect x="12" y="30" width="140" height="80" rx="12" fill="linear-gradient(135deg, #f15b41 0%, #fab184 100%)"/>
          <defs>
            <linearGradient id="earnGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f15b41"/>
              <stop offset="100%" stopColor="#fab184"/>
            </linearGradient>
          </defs>
          <rect x="12" y="30" width="140" height="80" rx="12" fill="url(#earnGrad)"/>
          <text x="82" y="52" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="9">Total Earned</text>
          <text x="82" y="82" textAnchor="middle" fill="white" fontSize="28" fontWeight="700">$1,250</text>
          <text x="82" y="100" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="8">From 8 activities</text>
          {/* Recent payments */}
          <text x="12" y="130" fill="#272726" fontSize="9" fontWeight="600">Recent</text>
          <rect x="12" y="140" width="140" height="40" rx="6" fill="white" stroke="#eee" strokeWidth="1"/>
          <text x="20" y="158" fill="#272726" fontSize="8">Research Survey</text>
          <text x="20" y="170" fill="#999" fontSize="7">Nov 24, 2025</text>
          <text x="140" y="158" textAnchor="end" fill="#2e7d32" fontSize="10" fontWeight="600">+$75</text>

          <rect x="12" y="186" width="140" height="40" rx="6" fill="white" stroke="#eee" strokeWidth="1"/>
          <text x="20" y="204" fill="#272726" fontSize="8">Focus Group</text>
          <text x="20" y="216" fill="#999" fontSize="7">Nov 20, 2025</text>
          <text x="140" y="204" textAnchor="end" fill="#2e7d32" fontSize="10" fontWeight="600">+$150</text>
          {/* Withdraw button */}
          <rect x="12" y="245" width="140" height="32" rx="8" fill="white" stroke="#272726" strokeWidth="1.5"/>
          <text x="82" y="266" textAnchor="middle" fill="#272726" fontSize="10" fontWeight="600">Withdraw Funds</text>
        </>
      )
    };
    return screens[step] || screens["1"];
  };

  return (
    <section
      ref={sectionRef}
      style={{
        backgroundColor: backgroundColor,
        padding: '60px 20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        position: 'relative'
      }}
    >
      {/* Subtle decorative background */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        height: '60%',
        background: 'radial-gradient(ellipse, rgba(241, 91, 65, 0.04) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <h2
          style={{
            fontFamily: '"Source Serif Pro", Georgia, serif',
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: '700',
            color: '#272726',
            textAlign: 'center',
            marginTop: '0',
            marginBottom: '48px'
          }}
        >
          {sectionTitle}
        </h2>

        {/* 2x2 Grid Layout */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '24px'
          }}
        >
          {steps.map((step, index) => (
            <div
              key={index}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '28px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
            >
              {/* Step number */}
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  border: '2px solid #e53e3e',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  // Add 0.15 offset so step 1 requires 15% progress before changing
                  backgroundColor: scrollProgress > (index * 0.22 + 0.15) ? '#e53e3e' : 'white',
                  color: scrollProgress > (index * 0.22 + 0.15) ? 'white' : '#e53e3e',
                  transition: 'all 0.4s ease',
                  boxShadow: scrollProgress > (index * 0.22 + 0.15) ? '0 3px 12px rgba(229, 62, 62, 0.35)' : 'none'
                }}
              >
                {scrollProgress > (index * 0.22 + 0.15) ? '✓' : step.num}
              </div>

              {/* Title */}
              <h3
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#272726',
                  marginTop: '0',
                  marginBottom: '8px'
                }}
              >
                {renderStepTitle(step.title, index)}
              </h3>

              {/* Short description */}
              <p
                style={{
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: '#666',
                  margin: '0 0 16px 0',
                  maxWidth: '280px'
                }}
              >
                {step.desc.split('.')[0]}.
              </p>

              {/* iPhone mockup */}
              <div style={{ marginTop: 'auto' }}>
                {step.imageUrl ? (
                  <img
                    src={step.imageUrl}
                    alt={step.title}
                    style={{
                      maxWidth: '120px',
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                ) : (
                  <StepIcon step={step.screen || step.num} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Responsive: stack on mobile */}
      <style>{`
        @media (max-width: 700px) {
          div[style*="grid-template-columns: repeat(2, 1fr)"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};
